import { useCallback } from 'react';
// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
import { useMutation, graphql } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { performPutRequest, performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { getDefaultOptions } from '@atlassian/jira-fetch/src/utils/fetch-default-options.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';

import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { ISSUE_ARCHIVE_API } from '../constants.tsx';

type ArchiveIssueAsyncResponse = {
	jira_archiveIssueAsync?: {
		success: boolean;
		taskId?: string;
		errors?: Array<{
			message?: string | null;
			extensions?: {
				statusCode?: number;
				__typename?: string;
			};
		}>;
	};
};

type onError = (analyticsEvent?: UIAnalyticsEvent) => void;
type onSuccess = (analyticsEvent?: UIAnalyticsEvent) => void;

const MAXIMUM_POLL_DURATION = 300000;
const DEFAULT_POLLING_DURATION = 2000;
const HTTP_ACCEPTED_STATUS_CODE = 202;

export const useArchiveIssue = () => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const cloudId = useCloudId();

	// GraphQL mutation for async archive
	// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
	const [commitArchiveIssueAsyncMutation] = useMutation(graphql`
		mutation services_archiveIssueAsyncMutation(
			$cloudId: ID!
			$input: JiraIssueArchiveAsyncInput!
		) {
			jira_archiveIssueAsync(cloudId: $cloudId, input: $input)
				@optIn(to: "JiraIssueViewRelayMigrations") {
				success
				errors {
					message
					extensions {
						... on JiraIssueArchiveAsyncErrorExtension {
							statusCode
							__typename
						}
					}
				}
			}
		}
	`);

	const pollForArchiveTaskStatus = useCallback(
		(
			urlForPollingStatus: string,
			onSuccess: onSuccess,
			onError: onError,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			const pollingStartTime = Date.now();
			let isPollingCompleted = false;

			const intervalId = setInterval(async () => {
				try {
					const pollingStatus = await performGetRequest(urlForPollingStatus);

					if (pollingStatus?.status === 'COMPLETED' || pollingStatus?.progress >= 100) {
						clearInterval(intervalId);
						onSuccess(analyticsEvent);
						isPollingCompleted = true;
					} else if (pollingStatus?.status === 'FAILED') {
						clearInterval(intervalId);
						onError(analyticsEvent);
						isPollingCompleted = true;
					}
					if (Date.now() - pollingStartTime >= MAXIMUM_POLL_DURATION) {
						clearInterval(intervalId);
						isPollingCompleted = true;
					}
				} catch {
					clearInterval(intervalId);
					onError(analyticsEvent);
					isPollingCompleted = true;
				}
			}, DEFAULT_POLLING_DURATION);

			if (isPollingCompleted) {
				fireTrackAnalytics(createAnalyticsEvent({}), 'polling completed', {
					action: 'issueArchivalUsingJQL',
					duration: Date.now() - pollingStartTime,
				});
			}
		},
		[createAnalyticsEvent],
	);

	const archiveAlongWithChildIssuesUsingJQL = useCallback(
		async (
			parentIssueKey: string | null,
			onSuccess: onSuccess,
			onError: onError,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			try {
				const response = await fetch(ISSUE_ARCHIVE_API, {
					method: 'POST',
					...getDefaultOptions(ISSUE_ARCHIVE_API),
					body: JSON.stringify({
						jql: `parent = ${parentIssueKey} OR issue = ${parentIssueKey}`,
					}),
				});

				const responseStatus = response.status;

				if (responseStatus !== HTTP_ACCEPTED_STATUS_CODE) {
					onError(analyticsEvent);
					return;
				}

				const urlForPollingStatus = await response.text();

				pollForArchiveTaskStatus(urlForPollingStatus, onSuccess, onError, analyticsEvent);
			} catch {
				onError(analyticsEvent);
			}
		},
		[pollForArchiveTaskStatus],
	);

	const archiveIssue = useCallback(
		async (
			issueKeys: (string | null)[],
			onSuccess: onSuccess,
			onError: onError,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			try {
				await performPutRequest(ISSUE_ARCHIVE_API, {
					body: JSON.stringify({
						issueIdsOrKeys: issueKeys,
					}),
				});
				onSuccess(analyticsEvent);
			} catch {
				onError(analyticsEvent);
			}
		},
		[],
	);

	/**
	 * Archives issues asynchronously using GraphQL mutation with JQL query.
	 * This function calls the jira_archiveIssueAsync mutation and handles polling
	 * for task completion, similar to archiveAlongWithChildIssuesUsingJQL.
	 *
	 * @param parentIssueKey - The parent issue key to archive along with child issues
	 * @param onSuccess - Callback function called when archiving succeeds
	 * @param onError - Callback function called when archiving fails
	 * @param analyticsEvent - Optional analytics event for tracking
	 */
	const archiveIssueAsync = useCallback(
		(
			parentIssueKey: string | null,
			onSuccess: onSuccess,
			onError: onError,
			analyticsEvent?: UIAnalyticsEvent,
		) => {
			commitArchiveIssueAsyncMutation({
				variables: {
					cloudId,
					input: {
						jql: `parent = ${parentIssueKey} OR issue = ${parentIssueKey}`,
					},
				},
				onCompleted: (response: ArchiveIssueAsyncResponse) => {
					if (response?.jira_archiveIssueAsync?.success) {
						const taskId = response?.jira_archiveIssueAsync?.taskId;
						if (taskId) {
							pollForArchiveTaskStatus(
								`/rest/api/3/task/${taskId}`,
								onSuccess,
								onError,
								analyticsEvent,
							);
						} else {
							onSuccess(analyticsEvent);
						}
					} else {
						onError(analyticsEvent);
					}
				},
				onError: () => {
					onError(analyticsEvent);
				},
			});
		},
		[commitArchiveIssueAsyncMutation, cloudId, pollForArchiveTaskStatus],
	);

	return { archiveIssue, archiveAlongWithChildIssuesUsingJQL, archiveIssueAsync };
};
